import { memo, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormCurrencyField from 'components/ui/form/FormCurrencyField';
import FormSelectField from 'components/ui/form/FormSelectField';
import useFormSubmit from 'hooks/useFormSubmit';

const validationSchema = yup.object({
  type: yup.number().required('settings.reminders.validations.type'),
  after_hour: yup.number().required('settings.reminders.validations.afterHour'),
  after_minute: yup
    .number()
    .required('settings.reminders.validations.afterMinute'),
});

const AddModal = (props) => {
  const { item, setItem, open, setOpen, setRefetch } = props;
  const { t } = useTranslation();
  const { submit, isSubmitting } = useFormSubmit();

  const formik = useFormik({
    initialValues: {
      type: '',
      after_hour: '',
      after_minute: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const method = item?.id ? 'postUpdate' : 'post';
      const message = item?.id
        ? 'settings.reminders.alerts.updated'
        : 'settings.reminders.alerts.added';
      submit(
        { type: method, contentType: 'simple' },
        values,
        '/jobs',
        {
          title: t(message),
        },
        item?.id,
        false,
        handleFinish
      );
    },
  });

  const handleFinish = () => {
    setRefetch(true);
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setItem(null);
    formik.resetForm();
  };

  useEffect(() => {
    if (item?.id) {
      formik.setValues({
        type: item.type || '',
        after_hour: item.after_hour || '',
        after_minute: item.after_minute || '',
      });
    }
  }, [item]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='lg'
      PaperProps={{
        className: '!base-border !shadow-none',
      }}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='!bg-gray-100 !mb-2 text-center'
      >
        <span className='pr-5'>
          {t(`settings.reminders.${item?.id ? 'editModal' : 'addModal'}.title`)}
        </span>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit} className='pt-2'>
          <Grid
            container
            spacing={{ xs: 2, sm: 2 }}
            rowSpacing={1}
            columns={{ xs: 12, sm: 12 }}
          >
            <Grid item={true} lg={12} sm={12} xs={12}>
              <FormSelectField
                delay={0}
                duration={0}
                fieldName='type'
                label={t('common.fields.type')}
                formik={formik}
                path={'/jobss/types'}
                isitemsizesmall={true}
              />
            </Grid>
            <Grid item={true} sm={6} xs={12}>
              <FormCurrencyField
                delay={0.1}
                label={t('common.fields.afterHours')}
                fieldName='after_hour'
                formik={formik}
              />
            </Grid>
            <Grid item={true} sm={6} xs={12}>
              <FormCurrencyField
                delay={0.2}
                label={t('common.fields.afterMinutes')}
                fieldName='after_minute'
                formik={formik}
              />
            </Grid>

            <Grid item={true} sm={12} xs={12}>
              <FormActionButtons
                delay={0.3}
                isSubmitting={isSubmitting}
                formType='dialog'
                setOpen={setOpen}
                reset={formik.resetForm}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(AddModal);
